import classnames from 'classnames';
import { withPropsOnChange, compose } from 'recompose';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Link from 'react-router/lib/Link';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import Joi from 'joi-full';
import moment from 'moment';
import { connect } from 'react-redux';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import { syncValidateData } from '../../../common/validator';
import Input from '../../../common/input';
import config from '../../../../config';
import { trimFunction } from '../../../common/trimData';
import { SHOP_CURRENCY } from '../../shops/constants';
import CountryCodePhoneInputRedux from '../../../common/countryCodePhoneInputRedux';
import FileInput from '../../../common/fileInput';
import withDateFormat from '../../../auth/utils/withDateFormat';
import withNotifications from '../../../common/withNotifications';

const regexMatchError = err => ({
  language: { string: { regex: { base: ` ${err}` } } },
});

const withForcedRequire = (rawSchemaObject) => {
  Object.keys(rawSchemaObject).forEach((key) => {
    rawSchemaObject[key] = rawSchemaObject[key].when('$strictFormValidation', {
      is: Joi.boolean().valid(true).required(),
      then: Joi.required(),
    });
  });

  return rawSchemaObject;
};

const baseSchema = {
  first_name: Joi.string().min(2).required().label('First name'),
  last_name: Joi.string().min(2).required().label('Last name'),
  date_of_birth: Joi.date().format(config.dateFormat).required().label('Date of birth'),
  type: Joi.string().allow(['individual', 'company']),
  email: Joi.string().optional().allow(null, ''),
  phone: Joi.string().optional().allow(null, ''),
  business_name: Joi.when('type', {
    is: 'company',
    then: Joi.string().min(3).required().label('Business name'),
  }),
};

const usaBankAccountSchema = Joi.object().keys(
  withForcedRequire(Object.assign({}, baseSchema, {
    personal_id_number: Joi.string().regex(/^[0-9]+$/).length(9).required()
      .label('Social Security Number'),
    routing_number: Joi.string().regex(/^[0-9]+$/).length(9).required()
      .label('Routing number')
      .options(regexMatchError('can only contain numbers')),
    account_number: Joi.string().regex(/^[0-9]+$/).min(8).max(17)
      .label('Account number')
      .options(regexMatchError('can only contain numbers')),
    ein: Joi.when('type', {
      is: 'company',
      then: Joi.string().required().regex(/^[0-9]{2}-[0-9]{7}$/).label('EIN')
        .options(regexMatchError("must contain numbers in format '##-#######'")),
    }),
    address: Joi.object().keys({
      line1: Joi.string().min(3).required().label('Street'),
      city: Joi.string().min(3).required().label('City'),
      state: Joi.string().length(2).required().label('State'),
      postal_code: Joi.string().min(5).required().label('Zip code'),
      country: Joi.string().length(2).required(),
    }),
  }))
);

const canadaBankAccountSchema = Joi.object().keys(
  withForcedRequire(Object.assign({}, baseSchema, {
    personal_id_number: Joi.string().regex(/^[0-9]+$/).length(9).optional()
      .label('Social Insurance Number'),
    routing_number: Joi.string().regex(/^[0-9]+$/).length(9).required()
      .label('Routing number')
      .options(regexMatchError('can only contain numbers')),
    account_number: Joi.string().regex(/^[0-9]+$/).min(5).max(12)
      .label('Account number')
      .options(regexMatchError('can only contain numbers')),
    ein: Joi.when('type', {
      is: 'company',
      then: Joi.string().required().regex(/^[0-9]+$/).label('Business tax id')
        .options(regexMatchError('can only contain numbers')),
    }),
    address: Joi.object().keys({
      line1: Joi.string().min(3).required().label('Street'),
      city: Joi.string().min(3).required().label('Municipality'),
      state: Joi.string().length(2).required().label('Province'),
      postal_code: Joi.string().min(3).required().label('Postal code'),
      country: Joi.string().length(2).required(),
    }),
  }))
);

const gbpBankAccountSchema = Joi.object().keys(
  withForcedRequire(Object.assign({}, baseSchema, {
    personal_id_number: Joi.string().optional().regex(/^([0-9]|[A-Za-z]{2}[0-9]{6}[A-Za-z]{1})+$/)
      .length(9)
      .label('National Insurance Number')
      .allow(null, '')
      .options(regexMatchError('can only contain numbers or 2 letters followed by 6 numbers and a letter')),
    routing_number: Joi.string().regex(/^[0-9]+$/).length(6).required()
      .label('Sort')
      .options(regexMatchError('can only contain numbers')),
    account_number: Joi.string().regex(/^[0-9]+$/).min(5).max(12)
      .label('Account number')
      .options(regexMatchError('can only contain numbers')),
    ein: Joi.when('type', {
      is: 'company',
      then: Joi.string().required().alphanum().max(10)
        .label('Company House Registration Number'),
    }),
    address: Joi.object().keys({
      line1: Joi.string().min(3).required().label('Street'),
      city: Joi.string().min(3).required().label('Post Town'),
      state: Joi.string().min(3).required().label('County'),
      postal_code: Joi.string().min(3).required().label('Postcode'),
      country: Joi.string().length(2).required(),
    }),
  }))
);

const audBankAccountSchema = canadaBankAccountSchema.keys(
  withForcedRequire({
    routing_number: Joi.string().regex(/^[0-9-]+$/).min(6).max(7).required()
      .label('BSB code')
      .options(regexMatchError('can only contain numbers')),
    address: Joi.object().keys({
      line1: Joi.string().min(3).required().label('Street'),
      city: Joi.string().min(3).required().label('City'),
      state: Joi.string().length(3).required().label('State'),
      postal_code: Joi.string().min(3).required().label('Postal code'),
      country: Joi.string().length(2).required(),
    }),
  })
);

const eurBankAccountSchema = canadaBankAccountSchema.keys(
  withForcedRequire({
    ein: Joi.when('type', {
      is: 'company',
      then: Joi.string().required().regex(/^[0-9]+$/).label('Business tax id')
        .options(regexMatchError('can only contain numbers')),
    }),
    account_number: Joi.string().regex(/^[A-Z0-9]{8}[0-9]{14}$/).label('IBAN')
      .options(regexMatchError('must be in required format')),
    personal_id_number: Joi.string().regex(/^(\d{7})([A-Z]{1,2})$/).optional()
      .allow(null, '')
      .label('PPS')
      .options(regexMatchError('Invalid PPS format')),
    address: Joi.object().keys({
      line1: Joi.string().min(3).required().label('Street'),
      city: Joi.string().min(3).required().label('City'),
      state: Joi.string().optional().label('County'),
      postal_code: Joi.string().min(3).optional().label('Eircode'),
      country: Joi.string().length(2).required(),
    }),
  })
);
const schemaByCurrency = {
  usd: usaBankAccountSchema,
  cad: canadaBankAccountSchema,
  gbp: gbpBankAccountSchema,
  aud: audBankAccountSchema,
  eur: eurBankAccountSchema,
};

const validate = (model, props) => {
  if (props.isEditPage) {
    return true;
  }

  const options = {
    context: {
      strictFormValidation: props.strictFormValidation,
    },
  };

  return syncValidateData(model, schemaByCurrency[props.currency], options);
};

const BANK_FORM = 'bankAccountForm';

const fields = [
  'routing_number',
  'transit_branch_number',
  'financial_institution_number',
  'account_number',
  'personal_id_number',
  'date_of_birth',
  'first_name',
  'last_name',
  'type',
  'email',
  'phone',
  'ein',
  'ein_provided',
  'business_name',
  'address',
];

const documentFields = [
  'photoIdFront',
  'photoIdBack',
  'businessRegistration',
];

const allowedDocumentMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
const documentFileInputAccept = allowedDocumentMimeTypes.join(', ');

class BankAccountForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bankAccountTouched: false,
    };
    this.saveBind = (...args) => this.save(...args);
  }

  componentDidMount() {
    if (this.props.changeExternalAccount) {
      this.clearBankAccount();
    }
  }

  save(data) {
    const model = pick(data, fields);
    const documents = reduce(pick(data, documentFields), (acc, doc, docName) => {
      // array is chrome and object in safari. life is good
      const docObject = doc && doc.length ? doc[0] : doc;
      if (docObject) {
        acc[docName] = docObject;
      }
      return acc;
    }, {});

    const { strictFormValidation, changeExternalAccount } = this.props;
    const shouldCheckDocs = strictFormValidation && !changeExternalAccount;
    if (shouldCheckDocs) {
      const { account } = this.props;
      const isFrontIdValid = (account && account.isFrontIdProvided()) ? true : !!documents.photoIdFront;
      const isBackIdValid = (account && account.isBackIdProvided()) ? true : !!documents.photoIdBack;

      const shouldValidateBusinessRegistration = (
        this.isBusinessRegistrationRequired()
          ? (!account || !account.isBusinessRegistrationProvided())
          : false
      );
      const isBusinessRegistrationValid = shouldValidateBusinessRegistration ? !!documents.businessRegistration : true;

      const areDocsValid = isBackIdValid && isFrontIdValid && isBusinessRegistrationValid;
      if (!areDocsValid) {
        this.props.addNotification({
          level: 'error',
          message: 'All documents are mandatory',
          title: 'Error!',
        });
        return;
      }

      const areDocTypesValid = Object.values(documents).every(file => allowedDocumentMimeTypes.includes(file.type));
      if (!areDocTypesValid) {
        this.props.addNotification({
          level: 'error',
          message: 'All documents should be in png, jpeg, jpg or pdf format',
          title: 'Error!',
        });
        return;
      }
    }

    if (this.isCurrencyCad()) {
      model.routing_number = `${model.transit_branch_number}-${model.financial_institution_number}`;
      delete model.transit_branch_number;
      delete model.financial_institution_number;
    }

    if (model.account_number && model.account_number[0] !== '*' && !this.isCurrencyGbp() && !this.isCurrencyAud()) {
      model.account_number = model.account_number.padStart(12, 0);
    }

    this.props.saveBankAccount(model, documents);
  }

  clearBankAccount = () => {
    if (!this.props.isEditPage || this.state.bankAccountTouched) return;
    this.props.change('account_number', '');
    if (this.isCurrencyCad()) {
      this.props.change('transit_branch_number', '');
      this.props.change('financial_institution_number', '');
    } else {
      this.props.change('routing_number', '');
    }
    this.setState({ bankAccountTouched: true });
  }

  isCurrencyUsd() {
    return this.props.currency === SHOP_CURRENCY.usd;
  }

  isCurrencyCad() {
    return this.props.currency === SHOP_CURRENCY.cad;
  }

  isCurrencyGbp() {
    return this.props.currency === SHOP_CURRENCY.gbp;
  }

  isCurrencyAud() {
    return this.props.currency === SHOP_CURRENCY.aud;
  }

  isCurrencyEur() {
    return this.props.currency === SHOP_CURRENCY.eur;
  }

  // whether a business registration document is needed in form
  isBusinessRegistrationRequired() {
    return this.props.type === 'company' && (this.isCurrencyGbp() || this.isCurrencyUsd());
  }

  einField() {
    const { account, isEditPage, currency } = this.props;

    if (this.props.type !== 'company') {
      return null;
    }

    const paramsByCurrency = {
      usd: {
        maxLength: 10,
        label: 'Employer Identification Number (EIN)',
        placeholder: '12-1234567',
      },
      cad: {
        maxLength: 9,
        label: 'Business Number (Tax ID)',
        placeholder: '000000000',
      },
      gbp: {
        maxLength: 10,
        label: 'Company House Registration Number',
        placeholder: '0000000000',
      },
      aud: {
        maxLength: 9,
        label: 'Company ACN/ABN',
        placeholder: '000000000',
      },
      eur: {
        maxLength: 6,
        label: 'Company number (Tax ID)',
        placeholder: '123456',
      },
    };

    const params = paramsByCurrency[currency];

    return (
      <div className="layout__item u-1/2-extra-extralarge-and-up">
        <div className="c-label-new">
          {params.label}
        </div>
        <Input
          className="c-input"
          type="text"
          name="ein"
          maxLength={params.maxLength}
          placeholder={params.placeholder}
        />
        <div display-if={isEditPage && account && account.einProvided()} className="u-type--bold u-fontsize-tiny" >
          NOTE: This value was previously provided.
        </div>
      </div>
    );
  }

  photoIds() {
    const { account } = this.props;
    const frontOpts = { label: 'Photo ID - Front' };
    const backOpts = { label: 'Photo ID - Back' };
    const isVerified = account && account.isVerified();

    if (isVerified) {
      if (account.isFrontIdProvided()) {
        frontOpts.className = 'is-disabled';
        frontOpts.label = `${frontOpts.label} (Provided)`;
      }

      if (account.isBackIdProvided()) {
        backOpts.className = 'is-disabled';
        backOpts.label = `${backOpts.label} (Provided)`;
      }
    }

    return (
      <div className="layout__item u-1/1 fs-mask">
        <div className="layout">
          <div className="layout__item u-1/2">
            <label htmlFor="photoIdFront" className="c-label-new u-mr-">{frontOpts.label}</label>
            <div>
              <FileInput
                name="photoIdFront"
                className={classnames(frontOpts.className, 'c-file-input__fixed')}
                accept={documentFileInputAccept}
              />
            </div>
          </div>
          <div className="layout__item u-1/2">
            <label htmlFor="photoIdBack" className="c-label-new u-mr-">{backOpts.label}</label>
            <div>
              <FileInput
                name="photoIdBack"
                className={classnames(backOpts.className, 'c-file-input__fixed')}
                accept={documentFileInputAccept}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  businessRegistration() {
    if (!this.isBusinessRegistrationRequired()) {
      return null;
    }

    const labelByCurrency = {
      [SHOP_CURRENCY.gbp]: 'Business registration',
      [SHOP_CURRENCY.usd]: 'Please upload the IRS EIN document',
    };

    const { account, currency } = this.props;
    let label = labelByCurrency[currency];
    if (account && account.isBusinessRegistrationProvided()) {
      label = `${label} (Provided)`;
    }

    return (
      <div className="layout__item u-1/2">
        <div className="u-lh-medium u-mb--">
          <label htmlFor="businessRegistration" className="c-label-new u-mr-">{label}</label>
          <div className="u-fontsize-tiny" display-if={this.isCurrencyUsd()}>(e.g., the SS-4 confirmation letter or Letter 147c)</div>
        </div>
        <div>
          <FileInput
            className="c-file-input__fixed"
            name="businessRegistration"
            accept={documentFileInputAccept}
          />
        </div>
      </div>
    );
  }

  routingNumber() {
    const { isEditPage, account, currency } = this.props;
    const accountInputDisabled = !this.state.bankAccountTouched && isEditPage && account;

    if (this.isCurrencyEur()) {
      return null;
    }

    if (!this.isCurrencyCad()) {
      const paramsByCurrency = {
        usd: {
          maxLength: 9,
          placeholder: '110000000',
          label: '9 Digits routing number',
        },
        gbp: {
          maxLength: 9,
          placeholder: '108800',
          label: 'Sort number',
        },
        aud: {
          maxLength: 7,
          placeholder: '110-000',
          label: 'BSB Code',
        },
      };

      const params = paramsByCurrency[currency];

      return (
        <div className="layout__item u-1/2-extra-extralarge-and-up">
          <div className="c-label-new">{params.label}</div>
          <Input
            className="c-input"
            type="text"
            name="routing_number"
            maxLength={params.maxLength}
            placeholder={params.placeholder}
            readOnly={accountInputDisabled}
            onClick={this.clearBankAccount}
            tabIndex={accountInputDisabled ? -1 : 0}
          />
        </div>
      );
    }

    return (
      <div className="layout__item u-1/2-extra-extralarge-and-up">
        <div className="layout">
          <div className="layout__item u-4/10">
            <div className="c-label-new">Transit branch number</div>
            <Input
              className="c-input"
              type="text"
              name="transit_branch_number"
              maxLength="5"
              placeholder="11000"
              readOnly={accountInputDisabled}
              onClick={this.clearBankAccount}
              tabIndex={accountInputDisabled ? -1 : 0}
            />
          </div>
          <div className="layout__item u-1/10 u-text-center">-</div>
          <div className="layout__item u-5/10">
            <div className="c-label-new">Financial institution number</div>
            <Input
              className="c-input"
              type="text"
              name="financial_institution_number"
              maxLength="3"
              placeholder="000"
              readOnly={accountInputDisabled}
              onClick={this.clearBankAccount}
              tabIndex={accountInputDisabled ? -1 : 0}
            />
          </div>
        </div>
      </div>
    );
  }

  personalIdNumber() {
    const { account, isEditPage, currency } = this.props;
    const accountVerified = account && account.isVerified();
    const opts = {
      type: 'number',
      placeholder: '000000000',
    };

    const params = {
      label: 'Social Security Number',
    };

    switch (currency) {
      case SHOP_CURRENCY.cad:
        params.label = 'Social Insurance Number';
        break;
      case SHOP_CURRENCY.gbp:
        params.label = 'National Insurance Number';
        opts.type = 'text';
        opts.placeholder = 'AB123456C';
        break;
      case SHOP_CURRENCY.aud:
        params.label = 'Tax File Number (TFN)';
        break;
      case SHOP_CURRENCY.eur: {
        params.label = 'PPS';
        opts.placeholder = '1234567A';
        opts.type = 'text';
        break;
      }
      default:
        break;
    }

    if (accountVerified) {
      opts.readOnly = true;
      opts.title = 'Not editable because the account is verified.';
    }

    return (
      <div className="layout__item u-1/2-extra-extralarge-and-up">
        <div className="c-label-new">
          {params.label}
        </div>
        <Input
          className="c-input"
          name="personal_id_number"
          maxLength="9"
          placeholder="000000000"
          {...opts}
        />
        <div display-if={isEditPage && account && account.personalIdNumberProvided()} className="u-fontsize-tiny u-type--bold">
          NOTE: This value was previously provided.
        </div>
      </div>
    );
  }

  address() {
    const addressFields = [];

    const byType = {
      individual: 'ID',
      company: 'Business Registration document',
    };
    addressFields.push((
      <div className="layout__item u-1/1">
        <div className="u-fontsize-small">
          The address provided <span className="u-type--bold u-type--underline">must match</span> the address listed on the {byType[this.props.type]} that is submitted above.
        </div>
      </div>
    ));

    addressFields.push((
      <div className="layout__item u-1/2-extra-extralarge-and-up" key="address">
        <div className="c-label-new">Street</div>
        <Input className="c-input" type="text" name="address.line1" />
      </div>
    ));

    if (this.isCurrencyGbp()) {
      addressFields.push((
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="city">
          <div className="c-label-new">Post Town</div>
          <Input className="c-input" type="text" name="address.city" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="state">
          <div className="c-label-new">County</div>
          <Input className="c-input" type="text" name="address.state" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="postal code">
          <div className="c-label-new">Postcode</div>
          <Input className="c-input" type="text" name="address.postal_code" />
        </div>
      ));
    }

    if (this.isCurrencyCad()) {
      addressFields.push((
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="city">
          <div className="c-label-new">Municipality</div>
          <Input className="c-input" type="text" name="address.city" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="state">
          <div className="c-label-new">Province</div>
          <Input className="c-input" type="text" name="address.state" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="zip code">
          <div className="c-label-new">Postal code</div>
          <Input className="c-input" type="text" name="address.postal_code" />
        </div>
      ));
    }

    if (this.isCurrencyUsd()) {
      addressFields.push((
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="city">
          <div className="c-label-new">City</div>
          <Input className="c-input" type="text" name="address.city" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="state">
          <div className="c-label-new">State</div>
          <Input className="c-input" type="text" name="address.state" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="zip code">
          <div className="c-label-new">Zip code</div>
          <Input className="c-input" type="text" name="address.postal_code" />
        </div>
      ));
    }

    if (this.isCurrencyAud()) {
      addressFields.push((
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="city">
          <div className="c-label-new">City</div>
          <Input className="c-input" type="text" name="address.city" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="state">
          <div className="c-label-new">State</div>
          <Input className="c-input" type="text" name="address.state" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="zip code">
          <div className="c-label-new">Postal Code</div>
          <Input className="c-input" type="text" name="address.postal_code" />
        </div>
      ));
    }

    if (this.isCurrencyEur()) {
      addressFields.push((
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="city">
          <div className="c-label-new">City</div>
          <Input className="c-input" type="text" name="address.city" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="state">
          <div className="c-label-new">County</div>
          <Input className="c-input" type="text" name="address.state" />
        </div>
      ), (
        <div className="layout__item u-1/2-extra-extralarge-and-up" key="zip code">
          <div className="c-label-new">Eircode</div>
          <Input className="c-input" type="text" name="address.postal_code" />
        </div>
      ));
    }

    addressFields.push((
      <div className="layout__item">
        <div className="c-label-new">Country</div>
        <Input className="c-input" type="text" name="address.country" disabled />
      </div>
    ));
    return addressFields;
  }

  accountNumberField() {
    const { isEditPage, account, currency } = this.props;
    const accountInputDisabled = !this.state.bankAccountTouched && isEditPage && account;
    const inputProps = {
      label: 'Your account number',
      placeholder: '000123456789',
      type: 'number',
    }
    switch (this.props.currency) {
      case SHOP_CURRENCY.eur:
        inputProps.label = 'IBAN';
        inputProps.placeholder = 'IE29AIBK93115212345678';
        inputProps.type = 'string';
        break;
      case SHOP_CURRENCY.gbp:
        inputProps.placeholder = '00012345';
        break;
      case SHOP_CURRENCY.aud:
        inputProps.placeholder = '000123456';
        break;
      default:
        break;
    }
    return (
      <div className="layout__item u-1/2-extra-extralarge-and-up">
        <div className="c-label-new">{inputProps.label}</div>
        <Input
          className="c-input"
          name="account_number"
          maxLength="22"
          readOnly={accountInputDisabled}
          onClick={this.clearBankAccount}
          tabIndex={accountInputDisabled ? -1 : 0}
          {...inputProps}
        />
      </div>
    );
  }

  render() {
    const {
      busy,
      handleSubmit,
      account,
      isEditPage,
      formErrors,
      type,
      cancelUrl,
      changeExternalAccount,
      dateFormat,
      onCancelClick,
    } = this.props;

    const accountInputDisabled = !changeExternalAccount && !this.state.bankAccountTouched && isEditPage && account;
    const accountVerified = account && account.isVerified();
    const opts = {};

    if (accountVerified) {
      opts.readOnly = true;
      opts.title = 'Not editable because the account is verified.';
    }

    return (
      <form onSubmit={handleSubmit(trimFunction(this.saveBind))}>
        <fieldset disabled={busy}>
          <div
            display-if={formErrors}
            className="c-login-error-message u-mb u-mt"
          >
            {formErrors}
          </div>
          <div className="layout u-mt" display-if={!changeExternalAccount}>
            <div className="layout__item u-1/1 c-heading--lightgrey">Basic info</div>
            <div className="layout__item u-1/2-extra-extralarge-and-up">
              <div className="c-label-new">Account type</div>
              <Field name="type" component="select" className="c-select">
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </Field>
            </div>
            <div
              display-if={type === 'company'}
              className="layout__item u-1/2-extra-extralarge-and-up"
            >
              <div className="c-label-new">Legal business name</div>
              <Input className="c-input" type="text" name="business_name" />
              <div className="u-fontsize-tiny" display-if={this.isCurrencyUsd()}>
                The name provided has to <span className="u-type--bold u-type--underline">exactly match</span> the name of the business entity on the IRS EIN document.
              </div>
            </div>
            {this.einField()}
            {this.businessRegistration()}
            <div className="layout__item u-1/2-extra-extralarge-and-up">
              <div className="c-label-new">First Name (as it appears on your ID)</div>
              <Input className="c-input" type="text" name="first_name" placeholder="John" {...opts} />
            </div>
            <div className="layout__item u-1/2-extra-extralarge-and-up">
              <div className="c-label-new">Last Name (as it appears on your ID)</div>
              <Input className="c-input" type="text" name="last_name" placeholder="Doe" {...opts} />
            </div>
            <div className="layout__item u-1/2-extra-extralarge-and-up">
              <div className="c-label-new">Date of birth</div>
              <Input
                className="c-input"
                type="text"
                placeholder={dateFormat}
                name="date_of_birth"
                datepicker={{
                  endDate: moment().subtract(18, 'year').toDate(),
                  startView: 2,
                  defaultViewDate: { year: 1990, month: 0, day: 1 },
                }}
                {...opts}
              />
            </div>
            <div className="layout__item u-1/2-extra-extralarge-and-up">
              <div className="c-label-new">Email</div>
              <Input className="c-input" type="email" name="email" placeholder="name@example.com" {...opts} />
            </div>
            <div className="layout__item u-1/2-extra-extralarge-and-up">
              <div className="c-label-new">Phone</div>
              <CountryCodePhoneInputRedux className="c-input" name="phone" {...opts} />
            </div>
            {this.personalIdNumber()}
            {this.photoIds()}
          </div>
          <div className="layout u-mt" display-if={!isEditPage || changeExternalAccount}>
            <div className="layout__item u-1/1 c-heading--lightgrey">Bank account info</div>
            {this.accountNumberField()}
            {this.routingNumber()}
          </div>
          <div className="layout u-mt" display-if={!changeExternalAccount}>
            <div className="layout__item u-1/1 c-heading--lightgrey">Address</div>
            {this.address()}
          </div>
          <div className="c-separator u-mv" />
          <div className="u-text-right">
            <Link to={cancelUrl}>
              <button
                type="button"
                onClick={onCancelClick}
                className="c-btn c-btn--grey u-width-120 u-display-block-tweak u-mb-- u-mr"
              >
                Cancel
              </button>
            </Link>
            <button type="submit" className="c-btn u-width-120 u-display-block-tweak u-mb--">
              <span className="c-btn__text">Save</span>
            </button>
          </div>
        </fieldset>
      </form>
    );
  }
}

BankAccountForm.propTypes = {
  saveBankAccount: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  type: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  account: PropTypes.objectOf(PropTypes.shape),
  isEditPage: PropTypes.bool,
  formErrors: PropTypes.node,
  cancelUrl: PropTypes.string.isRequired,
  currency: PropTypes.string,
  changeExternalAccount: PropTypes.bool,
  busy: PropTypes.bool.isRequired,
  onCancelClick: PropTypes.func,
};

BankAccountForm.defaultProps = {
  type: 'individual',
  currency: SHOP_CURRENCY.usd,
  changeExternalAccount: false,
  isEditPage: false,
  account: undefined,
  formErrors: undefined,
  onCancelClick: () => null,
};

const form = reduxForm({
  form: BANK_FORM,
  validate,
  enableReinitialize: true,
});

const selector = formValueSelector(BANK_FORM);

const mapStateToProps = state => ({
  type: selector(state, 'type'),
  // admins can submit form with less data
  strictFormValidation: !state.auth.isUserImpersonated,
});

export default compose(
  connect(mapStateToProps),
  form,
  withNotifications,
  withPropsOnChange(['type'], (props) => {
    if (props.isEditPage || props.currency !== SHOP_CURRENCY.gbp) return;

    if (props.type === 'individual') {
      props.change(
        'address', {
          line1: '',
          city: '',
          state: '',
          postal_code: '',
          country: props.initialValues.address.country,
        });
    } else {
      props.change('address', props.initialValues.address);
    }
  }),
  withDateFormat(),
)(BankAccountForm);
